import { FC, HTMLAttributes, isValidElement } from "react"

import clsx from "clsx"

import ErrorWithData from "@/utils/ErrorWithData"

import ErrorBoundary from "@/components/ErrorBoundary"

type FormError = {
  detail?: string
  response?: Response
  form?: Record<string, string>
  non_field_errors?: string[]
}

// For forms with nested objects, Django returns nested error messages, this should unpack them
const ErrorPathUnwrapper: FC<{
  path: string
  message: string | string[] | Record<string, string>
}> = ({ path, message }) => {
  const fieldRef: HTMLInputElement | null =
    (typeof window !== "undefined" && document.querySelector(`[name="${path}"]`)) || null
  if (path === "non_field_errors" && Array.isArray(message)) {
    return <li>{message.join("\n")}</li>
  }

  // If the message is a string or a React element, just render it
  if (typeof message === "string" || isValidElement(message)) {
    return <li>{message}</li>
  }

  //  For field errors, multiple errors are returned in an array of strings
  if (Array.isArray(message)) {
    return (
      <li>
        Error {fieldRef ? "with field" : "on field"}{" "}
        {fieldRef ? (
          <button
            type="button"
            className="font-normal underline"
            onClick={() => {
              fieldRef.focus()
              fieldRef.scrollIntoView({ block: "center" })
            }}
          >
            &ldquo;{path}&rdquo;
          </button>
        ) : (
          path
        )}
        :{" "}
        <ul>
          {message.map((m) => {
            return <ErrorPathUnwrapper key={m} path={path} message={m} />
          })}
        </ul>
      </li>
    )
  }

  // Otherwise, the errors are probably nested, and can be unpacked recursively
  if (typeof message === "object" && !!message) {
    return Object.entries(message).map(([k, v]) => (
      <ErrorPathUnwrapper key={["error", "children", path, k].join("_")} path={`${path}.${k}`} message={v} />
    ))
  }

  return <li>{JSON.stringify(message, null, 2)}</li>
}

const FILTERED_KEYS = ["response", "url", "limit_type"]

/**
 * ErrorUnwrapper component for displaying and unpacking nested error messages, especially from forms.
 * Handles Django-style nested errors and focuses fields on click.
 *
 * @param {Object} props - The props for the ErrorUnwrapper component.
 * @returns {JSX.Element} The rendered error messages.
 */

export const ErrorUnwrapper = ({
  error,
  className = "",
  ...props
}: { error: FormError | ErrorWithData | Error | null } & HTMLAttributes<HTMLDivElement | HTMLUListElement>) => {
  if (!error) {
    return null
  }
  if (typeof error === "string") {
    return (
      <div className={clsx(className, "whitespace-pre-wrap")} {...props}>
        {error}
      </div>
    )
  }
  if (Array.isArray(error) && error.every((e) => typeof e === "string")) {
    return error.join("\n")
  }

  if (Array.isArray(error)) {
    return (
      <ErrorBoundary>
        {error.map((e, idx) => (
          <ErrorUnwrapper key={idx} error={e} />
        ))}
      </ErrorBoundary>
    )
  }

  if (error && "detail" in error) {
    return <ErrorUnwrapper className={clsx(className)} {...props} error={error.detail as any} />
  }

  const visibleErrorKeys = Object.keys(error).filter((k) => !FILTERED_KEYS.includes(k))
  if (error && visibleErrorKeys.length > 0) {
    const visibleErrors: Record<string, any> = Object.entries(error)
      .filter(([k, v]) => !FILTERED_KEYS.includes(k) && v)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
    return (
      <ul className={clsx(Object.keys(visibleErrors).length > 1 && "list-disc pl-3", className)} {...props}>
        <ErrorBoundary>
          {Object.entries(visibleErrors ?? {}).map(([path, message]) => (
            <ErrorPathUnwrapper key={path} path={path} message={message} />
          ))}
        </ErrorBoundary>
      </ul>
    )
  }

  // Unknown error, but we have an API response to inspect
  if ("response" in error) {
    return (
      <ul className={clsx("list-disc pl-3", className)} {...props}>
        <li>Unhandled error. Don&apos;t worry, our team has been notified!</li>
        <li>Response code: {error.response?.status}</li>
        <li>Response type: {error.response?.statusText}</li>
      </ul>
    )
  }

  if (error && JSON.stringify(error) !== "{}") {
    return (
      <div {...props} className={clsx(className, "whitespace-pre-wrap")}>
        {JSON.stringify(error, null, 2)}
      </div>
    )
  }

  return null
}

export default ErrorUnwrapper
